import { addIceSafe, getIceConfig, loadIceConfig, packIce, packSdp } from './iceConfig'

export const createChatRtc = ({ getSocket, peerId, myId, onRemoteStream, onMode, onError }) => {
  let pc = null
  let localStream = null
  let remoteStream = new MediaStream()
  let pendingIce = []
  let makingOffer = false
  let queue = Promise.resolve()

  const polite = String(myId || '') > String(peerId || '')

  const send = (payload) => {
    getSocket().emit('chat:signal', { to: String(peerId), payload })
  }

  const fail = (err) => {
    if (onError) onError(err && err.message ? err.message : String(err || 'Call failed'))
  }

  const flushIce = async () => {
    if (!pc || !pc.remoteDescription) return
    const queued = pendingIce
    pendingIce = []
    for (const candidate of queued) {
      await addIceSafe(pc, candidate)
    }
  }

  const addLocalTracks = () => {
    if (!pc || !localStream) return
    localStream.getTracks().forEach((track) => {
      const sender = pc.getSenders().find((item) => item.track && item.track.kind === track.kind)
      if (sender) {
        if (sender.track !== track) sender.replaceTrack(track).catch(fail)
      } else {
        pc.addTrack(track, localStream)
      }
    })
  }

  const ensurePc = () => {
    if (pc) return pc
    pc = new RTCPeerConnection(getIceConfig())
    addLocalTracks()
    pc.onicecandidate = (event) => {
      const packed = packIce(event.candidate)
      if (packed && packed.candidate) send({ type: 'ice', candidate: packed })
    }
    pc.ontrack = (event) => {
      const track = event.track
      if (!track) return
      const existing = remoteStream.getTracks().find((item) => item.id === track.id || item.kind === track.kind)
      if (existing && existing !== track) {
        remoteStream.removeTrack(existing)
      }
      if (!remoteStream.getTracks().some((item) => item.id === track.id)) {
        remoteStream.addTrack(track)
      }
      if (onRemoteStream) onRemoteStream(remoteStream)
    }
    pc.onconnectionstatechange = () => {
      if (!pc) return
      if (pc.connectionState === 'failed') {
        pc.restartIce()
      }
    }
    return pc
  }

  const offer = async () => {
    const conn = ensurePc()
    addLocalTracks()
    makingOffer = true
    try {
      const next = await conn.createOffer()
      if (conn.signalingState !== 'stable' && conn.signalingState !== 'have-local-offer') return
      await conn.setLocalDescription(next)
      send({ type: 'offer', sdp: packSdp(conn.localDescription) })
    } finally {
      makingOffer = false
    }
  }

  const startMedia = async ({ video } = {}) => {
    await loadIceConfig()
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop())
      localStream = null
    }
    localStream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
      video: video ? { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } } : false
    })
    ensurePc()
    addLocalTracks()
    return localStream
  }

  const addVideo = async () => {
    const cam = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
    })
    const track = cam.getVideoTracks()[0]
    if (!track) return localStream
    if (!localStream) localStream = new MediaStream()
    localStream.getVideoTracks().forEach((current) => {
      current.stop()
      localStream.removeTrack(current)
    })
    localStream.addTrack(track)
    const conn = ensurePc()
    const existing = conn.getSenders().find((item) => item.track && item.track.kind === 'video')
    if (existing) await existing.replaceTrack(track)
    else conn.addTrack(track, localStream)
    if (onMode) onMode('video')
    send({ type: 'mode', kind: 'video' })
    await offer()
    return localStream
  }

  const dropVideo = async () => {
    if (localStream) {
      localStream.getVideoTracks().forEach((track) => {
        track.stop()
        localStream.removeTrack(track)
      })
    }
    if (pc) {
      pc.getSenders().forEach((sender) => {
        if (sender.track && sender.track.kind === 'video') {
          sender.replaceTrack(null).catch(() => {})
          try { pc.removeTrack(sender) } catch (_) {}
        }
      })
    }
    if (onMode) onMode('audio')
    send({ type: 'mode', kind: 'audio' })
    if (pc && pc.signalingState === 'stable') await offer()
    return localStream
  }

  const processSignal = async ({ from, payload }) => {
    if (!from || String(from) !== String(peerId) || !payload) return
    const conn = ensurePc()
    if (payload.type === 'mode') {
      if (onMode) onMode(payload.kind === 'video' ? 'video' : 'audio')
      return
    }
    if (payload.type === 'offer' && payload.sdp) {
      const collision = makingOffer || conn.signalingState !== 'stable'
      if (collision) {
        if (!polite) return
        try { await conn.setLocalDescription({ type: 'rollback' }) } catch (_) {}
      }
      await conn.setRemoteDescription(new RTCSessionDescription(payload.sdp))
      addLocalTracks()
      const answer = await conn.createAnswer()
      await conn.setLocalDescription(answer)
      send({ type: 'answer', sdp: packSdp(conn.localDescription) })
      await flushIce()
    } else if (payload.type === 'answer' && payload.sdp) {
      if (conn.signalingState !== 'have-local-offer') return
      await conn.setRemoteDescription(new RTCSessionDescription(payload.sdp))
      await flushIce()
    } else if (payload.type === 'ice' && payload.candidate) {
      if (!conn.remoteDescription) {
        pendingIce.push(payload.candidate)
        return
      }
      await addIceSafe(conn, payload.candidate)
    }
  }

  const handleSignal = (message) => {
    queue = queue.then(() => processSignal(message)).catch((err) => {
      const msg = String((err && err.message) || '')
      if (!/ice|candidate/i.test(msg)) fail(err)
    })
    return queue
  }

  const setMuted = (muted) => {
    if (!localStream) return
    localStream.getAudioTracks().forEach((track) => { track.enabled = !muted })
  }

  const getLocalStream = () => localStream

  const destroy = () => {
    pendingIce = []
    makingOffer = false
    remoteStream = new MediaStream()
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop())
      localStream = null
    }
    if (pc) {
      pc.onicecandidate = null
      pc.ontrack = null
      pc.onconnectionstatechange = null
      pc.close()
      pc = null
    }
  }

  return { startMedia, addVideo, dropVideo, offer, handleSignal, setMuted, getLocalStream, destroy }
}
